import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios'; // ✅ Import axios for API calls
import { UserContext } from '../../UserContext'; // ✅ Import User Context

const IntermittentFasting = () => {
  const { user } = useContext(UserContext); // ✅ Get user from context
  const category = 'intermittentfasting'; // ✅ Category for fetching videos
  const [videos, setVideos] = useState([]); // ✅ State to hold videos
  const [loading, setLoading] = useState(true); // ✅ Loading state

  useEffect(() => {
    const fetchVideos = async () => {
      try {
        const response = await axios.get(`https://wholeness-forward.onrender.com/api/video/videos/${category}`);
        if (Array.isArray(response.data)) {
          setVideos(response.data);
        }
      } catch (error) {
        console.error('Error fetching videos:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchVideos();
  }, []);

  // ✅ Handle Mark as Helpful for logged-in users only
  const handleMarkHelpful = async (videoId) => {
    if (!user || !user.id) {
      alert('You must be logged in to mark as helpful.');
      return;
    }

    try {
      const response = await axios.post(`https://wholeness-forward.onrender.com/api/video/videos/toggleHelpful`, {
        videoId,
        userId: user.id,
      });

      // ✅ Update UI with new helpful count
      setVideos((prevVideos) =>
        prevVideos.map((video) =>
          video._id === videoId
            ? { ...video, helpfulCount: response.data.helpfulCount }
            : video
        )
      );
    } catch (error) {
      console.error('Error marking video as helpful:', error);
    }
  };

  return (
    <div className="intermittent-fasting-page page-container">
      <h1 className="page-title">Intermittent Fasting</h1>
      <p className="page-description">
        Welcome to the Intermittent Fasting section of Wholeness Forward.
      </p>

      <img
        src="/images/wf-intermittentfasting.webp"
        alt="Intermittent Fasting Illustration"
        className="page-image"
        width="330"
      />

      <p className="page-description">
        Intermittent fasting (IF) is an eating pattern where you cycle between periods of eating and fasting. 
        Unlike traditional diets, IF focuses on when to eat rather than what to eat.
      </p>

      <hr className="section-divider" />

      <h2 className="section-title">Common Methods of Intermittent Fasting</h2>
      <ul className="content-list">
        <li><strong>16/8 Method:</strong> Fast for 16 hours and eat during an 8-hour window (e.g., 12 PM - 8 PM).</li>
        <li><strong>5:2 Diet:</strong> Eat normally for 5 days and limit calorie intake on the other 2 days.</li>
        <li><strong>Alternate-Day Fasting:</strong> Eat normally every other day and fast on in-between days.</li>
        <li><strong>Eat-Stop-Eat:</strong> Fast for a full 24 hours once or twice a week.</li>
      </ul>

      <h3 className="section-title">Benefits of Intermittent Fasting</h3>
      <ul className="content-list">
        <li>Promotes weight loss and fat burning</li>
        <li>Improves insulin sensitivity and blood sugar control</li>
        <li>Enhances cellular repair processes and autophagy</li>
        <li>May reduce inflammation and improve heart health</li>
        <li>Could support brain health and longevity</li>
      </ul>

      <hr className="section-divider" />

      <h2 className="section-title">Curated Video Collection</h2>

      {loading ? (
        <p className="loading-message">Loading videos...</p>
      ) : (
        <div className="video-container">
          {videos.length > 0 ? (
            videos.map((video) => (
              <div key={video._id} className="video-card">
                <h3 className="video-title">{video.title}</h3>
                <p className="video-details">
                  <strong>Source:</strong> {video.source} <br />
                  <strong>Length:</strong> {video.length} <br />
                  {video.description}
                </p>
                <iframe
                  className="video-frame"
                  src={video.url}
                  title={video.title}
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                ></iframe>

                {/* ✅ Helpful Button */}
                <button
                  className="button"
                  onClick={() => handleMarkHelpful(video._id)}
                  disabled={video.usersWhoClickedHelpful?.includes(user?.id)}
                >
                  {video.usersWhoClickedHelpful?.includes(user?.id)
                    ? `Helpful (${video.helpfulCount})`
                    : `Mark as Helpful (${video.helpfulCount || 0})`}
                </button>
              </div>
            ))
          ) : (
            <p className="no-videos-message">No videos found for this category.</p>
          )}
        </div>
      )}

      <hr className="section-divider" />

      <div className="forum-section">
        <h2 className="section-title">Join the Intermittent Fasting Discussion</h2>
        <p className="section-description">
          Have questions or insights about intermittent fasting? Join our forum to connect with others who are exploring this topic.
        </p>
        <a href="/forum/Intermittent_Fasting" className="forum-link">
          Go to Intermittent Fasting Forum
        </a>
      </div>
    </div>
  );
};

export default IntermittentFasting;
